import { useAuth0 } from '@auth0/auth0-react';
import type { CheckListFilter, CheckOutcome } from '@my/check-api';
import { useChecks } from '@my/check-api/hooks';
import { ErrorBanner, Loading, PageHeader } from '@my/ui';
import { ChevronRight, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import {
  OUTCOME_BADGE,
  OUTCOME_DOT,
  STATUS_BADGE,
  formatWhen,
  startOfDaysAgo,
} from './lib/checkDisplay';

// ────────────────────────────────────────────────────────────────────────────
// Recorded checks — live against /check/api/v1/checks.
//
// Read-only by design: a check is created by a driver in myDriver, so there is
// no "New check" action here. One row per inspected resource rather than per
// walk-around session, so a trailer's history reads independently of whatever
// unit it was coupled to.
//
// Filtering is server-side (the BE caps at 200 rows) rather than fetch-all-
// then-filter, because this table grows by every vehicle every day.
// ────────────────────────────────────────────────────────────────────────────

type RangeKey = 'today' | 'week' | 'month' | 'all';

const RANGES: { key: RangeKey; days: number | null }[] = [
  { key: 'today', days: 0 },
  { key: 'week', days: 7 },
  { key: 'month', days: 30 },
  { key: 'all', days: null },
];

const OUTCOMES: CheckOutcome[] = ['Pass', 'Defect', 'Advisory'];

export function CheckListPage() {
  const { t } = useTranslation('checks');
  const { user } = useAuth0();
  const navigate = useNavigate();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);

  const [range, setRange] = useState<RangeKey>('week');
  const [outcome, setOutcome] = useState<CheckOutcome | 'all'>('all');
  const [search, setSearch] = useState('');

  // Debouncing is deliberately skipped: the BE search is indexed and capped,
  // and React Query dedupes in-flight requests for the same key.
  const filter: CheckListFilter = useMemo(() => {
    const days = RANGES.find((r) => r.key === range)?.days ?? null;
    return {
      ...(days !== null ? { fromUtc: startOfDaysAgo(days) } : {}),
      ...(outcome !== 'all' ? { outcome } : {}),
      ...(search.trim() ? { search: search.trim() } : {}),
    };
  }, [range, outcome, search]);

  const listQuery = useChecks(orgId, filter);
  const rows = listQuery.data ?? [];

  const hasFilters = range !== 'week' || outcome !== 'all' || search.trim() !== '';
  const clear = () => {
    setRange('week');
    setOutcome('all');
    setSearch('');
  };

  if (!orgId) {
    return (
      <Shell>
        <ErrorBanner title={t('templates.noOrg.title')} message={t('templates.noOrg.message')} />
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="flex flex-wrap items-center gap-2 pb-3">
        {RANGES.map((r) => (
          <button
            key={r.key}
            type="button"
            onClick={() => setRange(r.key)}
            className={chipClass(range === r.key)}
          >
            {t(`list.range.${r.key}` as 'list.range.today')}
          </button>
        ))}

        <span className="h-4 w-px bg-slate-300" aria-hidden="true" />

        <button
          type="button"
          onClick={() => setOutcome('all')}
          className={chipClass(outcome === 'all')}
        >
          {t('list.filters.all')}
        </button>
        {OUTCOMES.map((o) => (
          <button
            key={o}
            type="button"
            onClick={() => setOutcome(o)}
            className={`inline-flex items-center gap-1 ${chipClass(outcome === o)}`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${OUTCOME_DOT[o]}`} aria-hidden="true" />
            {t(`list.outcome.${o.toLowerCase()}` as 'list.outcome.pass')}
          </button>
        ))}

        {hasFilters && (
          <button
            type="button"
            onClick={clear}
            className="text-[10px] text-slate-400 underline hover:text-slate-600"
          >
            {t('list.filters.clear')}
          </button>
        )}

        <div className="flex-1" />

        <div className="relative">
          <Search
            className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
            aria-hidden="true"
          />
          <input
            type="text"
            value={search}
            placeholder={t('list.search')}
            onChange={(e) => setSearch(e.target.value)}
            className="w-56 rounded-full border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>
      </div>

      {listQuery.isPending && <Loading label={t('list.title')} />}

      {listQuery.isError && (
        <ErrorBanner title={t('list.loadFailed')} message={listQuery.error.message} />
      )}

      {listQuery.isSuccess && (
        <div className="overflow-x-auto rounded-md">
          <div className="min-w-[880px]">
            <table className="w-full table-auto text-xs">
              <thead className="bg-slate-700 text-[11px] uppercase tracking-wide text-white">
                <tr>
                  <Th>{t('list.grid.when')}</Th>
                  <Th>{t('list.grid.vehicle')}</Th>
                  <Th>{t('list.grid.driver')}</Th>
                  <Th>{t('list.grid.template')}</Th>
                  <Th align="center">{t('list.grid.outcome')}</Th>
                  <Th align="center">{t('list.grid.failed')}</Th>
                  <Th align="center">{t('list.grid.odometer')}</Th>
                  <Th align="center">{t('list.grid.status')}</Th>
                  <th className="sticky right-0 z-10 w-10 bg-slate-700 px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-500 bg-white">
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-3 py-6 text-center text-xs italic text-slate-400">
                      {hasFilters ? t('list.emptyFiltered') : t('list.empty')}
                    </td>
                  </tr>
                ) : (
                  rows.map((c) => (
                    <tr
                      key={c.id}
                      className="group cursor-pointer hover:bg-slate-50"
                      title={t('list.grid.doubleClickHint')}
                      onDoubleClick={() => navigate(`/checks/${c.id}`)}
                    >
                      <td className="whitespace-nowrap px-3 py-1.5 text-xs text-slate-600">
                        {formatWhen(c.completedUtc ?? c.startedUtc)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5">
                        <span className="rounded-sm border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px] font-medium text-slate-700">
                          {c.resourceRegistration}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-xs text-slate-700">
                        {c.performedByName}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-xs text-slate-600">
                        {c.templateName}
                        <span className="ml-1 text-[10px] text-slate-400">v{c.templateVersion}</span>
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        <Badge className={OUTCOME_BADGE[c.outcome]}>
                          {t(`list.outcome.${c.outcome.toLowerCase()}` as 'list.outcome.pass')}
                        </Badge>
                      </td>
                      <td className="px-3 py-1.5 text-center text-xs">
                        {/* Only worth colouring when there is something wrong. */}
                        {c.failedCount > 0 ? (
                          <span className="font-semibold text-red-600">{c.failedCount}</span>
                        ) : (
                          <span className="text-slate-400">0</span>
                        )}
                        <span className="text-slate-400">/{c.answerCount}</span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-center text-xs text-slate-600">
                        {c.odometerKm != null ? c.odometerKm.toLocaleString() : '—'}
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        <Badge className={STATUS_BADGE[c.status]}>
                          {t(
                            c.status === 'InProgress'
                              ? 'list.status.inProgress'
                              : 'list.status.submitted',
                          )}
                        </Badge>
                      </td>
                      <td className="sticky right-0 z-10 bg-white px-3 py-1.5 text-right group-hover:bg-slate-50">
                        <button
                          type="button"
                          aria-label={t('list.grid.open')}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/checks/${c.id}`);
                          }}
                          className="inline-flex h-5 w-5 items-center justify-center rounded-sm text-blue-600 hover:bg-blue-50"
                        >
                          <ChevronRight className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* The BE caps the result set; say so rather than silently truncating. */}
      {listQuery.isSuccess && rows.length >= 200 && (
        <p className="pt-2 text-[10px] italic text-slate-400">{t('list.capped')}</p>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('checks');
  return (
    <div className="flex h-full min-h-0 flex-col px-2">
      <PageHeader title={t('list.title')} description={t('list.description')} />
      <main className="flex min-h-0 flex-1 flex-col overflow-auto pb-4">{children}</main>
    </div>
  );
}

function Th({ children, align }: { children: React.ReactNode; align?: 'center' | 'right' }) {
  const cls = align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left';
  return <th className={`whitespace-nowrap px-3 py-2 font-semibold ${cls}`}>{children}</th>;
}

function Badge({ className, children }: { className: string; children: React.ReactNode }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${className}`}
    >
      {children}
    </span>
  );
}

function chipClass(active: boolean): string {
  return `rounded-full border px-2.5 py-0.5 text-[10px] font-medium transition-colors ${
    active
      ? 'border-slate-700 bg-slate-700 text-white'
      : 'border-slate-300 bg-white text-slate-500 hover:border-slate-500 hover:text-slate-700'
  }`;
}
